import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'bt_install_prompt_dismissed';
const VISIT_KEY = 'bt_install_prompt_visits';

export default function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    try {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches === true
        || (window.navigator as any).standalone === true;
      if (isStandalone) return;

      const dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      const visits = parseInt(localStorage.getItem(VISIT_KEY) || '0', 10);

      if (dismissedAt && now - dismissedAt < sevenDays && visits < 5) {
        return;
      }

      localStorage.setItem(VISIT_KEY, String(visits + 1));

      const handleBeforeInstallPrompt = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setShowPrompt(true);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    } catch {
      // silent
    }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowPrompt(false);
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // silent
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // silent
    }
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-slide-up">
      <div className="bg-[var(--glass-card-bg,#ffffff)] rounded-2xl shadow-2xl border border-[var(--glass-card-border,rgba(34,42,53,0.08))] overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-ocean-100 rounded-xl flex items-center justify-center">
              <Smartphone className="h-6 w-6 text-ocean-600" />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[var(--glass-text-primary,#242424)]">Installa Sea Level B2B</h3>
              <p className="text-sm text-[var(--glass-text-secondary,#898989)] mt-1">
                Aggiungi l'app alla home screen per un accesso più rapido
              </p>
            </div>

            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1 text-[var(--glass-text-muted,#898989)] hover:text-[var(--glass-text-secondary,#898989)]"
              aria-label="Ignora"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleInstall}
              className="flex-1 btn-primary py-2.5 text-sm"
              type="button"
            >
              <Download className="h-4 w-4 mr-1.5 inline" />
              Installa
            </button>
            <button
              onClick={handleDismiss}
              className="btn-secondary py-2.5 text-sm"
              type="button"
            >
              Dopo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
